import {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { useChat } from '@ai-sdk/react';
import { useAppDispatch, useAppSelector } from 'store/hooks/useApp';
import aiChatsApi from 'store/slices/aiChats/apis/aiChatsApi';
import AI_CHATS_LIST_TAG, { AI_CHAT_MESSAGES_TAG } from 'store/tags/aiChats';

import createAiChatTransport from '../utils/createAiChatTransport';
import readStreamError from '../utils/readStreamError';

import type IAiChatConversationOptions from '../interfaces/IAiChatConversationOptions';
import type IAiChatConversationState from '../interfaces/IAiChatConversationState';
import type { IAiChatResponseOpen } from '../interfaces/IAiChatTransportOptions';
import type { TAiChatUiMessage } from '../interfaces/IAiChatUiMessage';

/** How the last send ended, as `onFinish` reported it. Reset before each send. */
interface ISendOutcome {
  /** True when the API accepted the message and its reply ended without an error or an abort. */
  isSent: boolean,
  /** Id the reply named on its `start` chunk, or null when it named none. */
  createdChatId: number | null,
}

const NO_SEND: ISendOutcome = { isSent: false, createdChatId: null };

/**
 * Shown when a reply into a new chat carried no chat id. The API names the chat on every reply,
 * so this is a version mismatch between the two repos, not something the user did.
 */
const UNNAMED_CHAT_ERROR = 'The reply did not name its chat.';

/**
 * One conversation with the assistant: the messages so far, and a way to send the next one.
 *
 * Wraps `useChat`. The SDK owns the message list and the stream: the message sent shows at once,
 * the reply grows chunk by chunk, and both stay in `messages` once the stream ends. What this hook
 * adds is the contract the panel needs — one send at a time, the adoption of a chat the send
 * created, and the cache tags that tell the rest of the app the chat moved on.
 *
 * The client side of the sequence diagram:
 * @see [6. Flow: one turn](../../../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
export default function useAiChatConversation({
  chatId, initialMessages, chatContext, onChatCreated, onToolOutput,
}: IAiChatConversationOptions): IAiChatConversationState {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(({ auth }) => auth.userAccessToken || auth.accessToken);
  const [isAdopting, setIsAdopting] = useState<boolean>(false);
  const [contractError, setContractError] = useState<string | null>(null);
  // Read by the transport and the callbacks at request time, so a transport built once on mount
  // sees the chat, the context, and the token of the send, not of the render it was built in.
  const latest = useRef({
    accessToken, chatId, chatContext, onToolOutput,
  });
  latest.current = {
    accessToken, chatId, chatContext, onToolOutput,
  };
  const outcome = useRef<ISendOutcome>(NO_SEND);
  // Read inside the send itself, where state would still hold the value of the render it started in.
  const isSendingRef = useRef<boolean>(false);

  const handleResponseOpen = useCallback(({ isOk }: IAiChatResponseOpen) => {
    if (!isOk) {
      return;
    }

    // Sending happens outside RTK Query, so nothing else tells the cache the list has moved on.
    // The server has bumped the chat by the time the response opens, so the history is refetched
    // now rather than when the reply ends: opened mid-reply, it shows the chat at the top with
    // the message just sent.
    dispatch(aiChatsApi.util.invalidateTags([AI_CHATS_LIST_TAG]));
  }, [dispatch]);

  const transport = useMemo(() => createAiChatTransport({
    apiBase: process.env.REACT_APP_API ?? '',
    getAccessToken: () => latest.current.accessToken,
    getChatId: () => latest.current.chatId,
    getChatContext: () => latest.current.chatContext,
    onResponseOpen: handleResponseOpen,
    onToolOutput: (toolOutput) => latest.current.onToolOutput?.(toolOutput),
  }), [handleResponseOpen]);

  const {
    messages, status, error, sendMessage, clearError,
  } = useChat<TAiChatUiMessage>({
    messages: initialMessages,
    transport,
    onFinish: ({ message, isAbort, isError }) => {
      const namedChatId = message.metadata?.chatId;

      outcome.current = {
        isSent: !isAbort && !isError,
        // Only a number is a chat id. Anything else the stream put there is not adopted.
        createdChatId: typeof namedChatId === 'number' ? namedChatId : null,
      };

      // The rows of the chat changed under the cache. Read again, so a later open of this chat
      // seeds from what the API stored, the reply included.
      const finishedChatId = latest.current.chatId;

      if (finishedChatId && !isError) {
        dispatch(aiChatsApi.util.invalidateTags([{ type: AI_CHAT_MESSAGES_TAG, id: finishedChatId }]));
      }
    },
  });

  const send = useCallback(async (text: string): Promise<number | null> => {
    // One at a time. A second message sent before the first has named its chat would create a
    // second chat and split the history between them.
    if (isSendingRef.current) {
      return null;
    }

    isSendingRef.current = true;
    outcome.current = NO_SEND;
    setContractError(null);
    clearError();

    try {
      // Never rejects: the SDK folds every failure into `status`, `error`, and `onFinish`.
      await sendMessage({ text });

      const { isSent, createdChatId } = outcome.current;

      // Nothing was written that is worth going back to, or the chat was the caller's already.
      // The message stays on screen next to the error rather than vanishing with it.
      if (!isSent || latest.current.chatId) {
        return null;
      }

      // Not silent: without the id every later message would open a chat of its own.
      if (!createdChatId) {
        setContractError(UNNAMED_CHAT_ERROR);

        return null;
      }

      // Awaited, so the composer stays shut until the chat is the conversation's own.
      setIsAdopting(true);

      try {
        await onChatCreated?.(createdChatId);
      } finally {
        setIsAdopting(false);
      }

      return createdChatId;
    } finally {
      isSendingRef.current = false;
    }
  }, [clearError, onChatCreated, sendMessage]);

  return {
    messages,
    isStreaming: status === 'submitted' || status === 'streaming',
    isSending: status === 'submitted' || status === 'streaming' || isAdopting,
    streamError: error ? readStreamError(error) : contractError,
    send,
  };
}
